import React, { useState } from 'react';
import { Database, Download, Eye, Search, Filter, FileText } from 'lucide-react';

const EquipmentDataSheets = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  const equipmentDataSheets = [
    {
      id: 1,
      tag: 'T-101',
      name: 'Distillation Column',
      type: 'Column',
      manufacturer: 'Sulzer Ltd.',
      model: 'TD-2500',
      specifications: {
        height: '25 m',
        diameter: '2.5 m',
        pressure: '5 bar',
        temperature: '180°C',
        material: 'SS 316L',
        trays: '40 Sieve Trays'
      },
      installDate: '2023-06-15',
      lastInspection: '2025-10-20',
      status: 'Active',
      documentNumber: 'DS-T-101-2024'
    },
    {
      id: 2,
      tag: 'E-101',
      name: 'Reboiler',
      type: 'Heat Exchanger',
      manufacturer: 'Alfa Laval',
      model: 'CB76-150H',
      specifications: {
        duty: '2.5 MW',
        area: '150 m²',
        pressure: '6 bar (shell/tube)',
        temperature: '190°C / 200°C',
        material: 'SS 316 / Carbon Steel',
        type: 'Shell & Tube'
      },
      installDate: '2023-06-20',
      lastInspection: '2025-11-01',
      status: 'Active',
      documentNumber: 'DS-E-101-2024'
    },
    {
      id: 3,
      tag: 'R-201',
      name: 'CSTR Reactor',
      type: 'Reactor',
      manufacturer: 'Buss-SMS-Canzler',
      model: 'CSTR-50',
      specifications: {
        volume: '50 m³',
        pressure: '15 bar',
        temperature: '180°C',
        material: 'Hastelloy C-276',
        agitator: 'Anchor Type, 75 RPM',
        jacket: 'Half-pipe jacket'
      },
      installDate: '2023-05-10',
      lastInspection: '2025-09-15',
      status: 'Active',
      documentNumber: 'DS-R-201-2024'
    },
    {
      id: 4,
      tag: 'P-201',
      name: 'Feed Pump',
      type: 'Pump',
      manufacturer: 'Grundfos',
      model: 'CR 64-5',
      specifications: {
        flow: '100 m³/h',
        head: '50 m',
        power: '45 kW',
        speed: '2950 RPM',
        material: 'SS 316',
        impeller: '5-stage'
      },
      installDate: '2023-06-25',
      lastInspection: '2025-11-10',
      status: 'Active',
      documentNumber: 'DS-P-201-2024'
    },
    {
      id: 5,
      tag: 'V-301',
      name: 'Storage Vessel',
      type: 'Vessel',
      manufacturer: 'Boardman LLC',
      model: 'V-100-SS',
      specifications: {
        capacity: '100 m³',
        pressure: 'Atmospheric',
        temperature: '50°C',
        material: 'SS 304',
        orientation: 'Vertical',
        roof: 'Fixed Cone Roof'
      },
      installDate: '2023-04-15',
      lastInspection: '2025-08-20',
      status: 'Active',
      documentNumber: 'DS-V-301-2024'
    }
  ];

  const equipmentTypes = [
    { id: 'all', name: 'All Equipment', count: equipmentDataSheets?.length },
    { id: 'column', name: 'Columns', count: 1 },
    { id: 'heat-exchanger', name: 'Heat Exchangers', count: 1 },
    { id: 'reactor', name: 'Reactors', count: 1 },
    { id: 'pump', name: 'Pumps', count: 1 },
    { id: 'vessel', name: 'Vessels', count: 1 }
  ];

  const filteredEquipment = equipmentDataSheets?.filter(eq =>
    eq?.tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    eq?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    eq?.type?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by tag, name, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50">
              <Filter className="w-5 h-5 text-gray-600" />
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
              Add Equipment
            </button>
          </div>
        </div>

        {/* Type Filters */}
        <div className="flex flex-wrap gap-2 mt-4">
          {equipmentTypes?.map((type) => (
            <button
              key={type?.id}
              onClick={() => setSelectedType(type?.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedType === type?.id
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {type?.name} ({type?.count})
            </button>
          ))}
        </div>
      </div>
      {/* Equipment Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredEquipment?.map((equipment) => (
          <div key={equipment?.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Database className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{equipment?.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">Tag: {equipment?.tag}</p>
                    <p className="text-sm text-gray-600">Document: {equipment?.documentNumber}</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  {equipment?.status}
                </span>
              </div>

              {/* Type Badge */}
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-lg">
                  {equipment?.type}
                </span>
              </div>

              {/* Manufacturer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Manufacturer:</span>
                    <span className="ml-2 font-medium text-gray-900">{equipment?.manufacturer}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Model:</span>
                    <span className="ml-2 font-medium text-gray-900">{equipment?.model}</span>
                  </div>
                </div>
              </div>

              {/* Specifications */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Specifications</h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(equipment?.specifications)?.slice(0, 4)?.map(([key, value]) => (
                    <div key={key} className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 capitalize mb-1">{key?.replace(/_/g, ' ')}</p>
                      <p className="text-sm font-semibold text-gray-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="flex items-center justify-between text-sm mb-4">
                <div>
                  <span className="text-gray-600">Installed:</span>
                  <span className="ml-2 font-medium text-gray-900">{equipment?.installDate}</span>
                </div>
                <div>
                  <span className="text-gray-600">Last Inspection:</span>
                  <span className="ml-2 font-medium text-gray-900">{equipment?.lastInspection}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
                <button className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                  <Eye className="w-4 h-4" />
                  <span>View Full Datasheet</span>
                </button>
                <button className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* No Results */}
      {filteredEquipment?.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No equipment found</h3>
          <p className="text-gray-600">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default EquipmentDataSheets;